/**
 * Card header strip — the titled band across the top of a full-bleed card
 * (a table card, a list card), separated from the content by a border.
 *
 *   <Card padding="none" className="overflow-hidden">
 *     <CardHeader title={`Invoices (${rows.length})`} actions={<Button size="sm" variant="secondary">Refresh</Button>} />
 *     <Table compact>…</Table>
 *   </Card>
 *
 * Why a primitive: the 10-31 sweep produced two different strips for the same
 * job. One padded only the top (`pt-4`, `sm:pt-6`) with `border-b` and leaned on the
 * SectionHeading's own mb-4 for the bottom gap; the other was `px-4 py-3
 * border-b` with `<SectionHeading className="!mb-0">`. They render at
 * different heights, and the second needs an `!important` override because
 * SectionHeading's margin cannot be removed through className (Tailwind
 * resolves same-specificity utilities by stylesheet order). This owns the
 * padding, the border and the heading margin, so neither hack is needed.
 *
 * Horizontal padding matches Card's `md` bucket (px-4 sm:px-6), so the title
 * lines up with the content of an ordinary padded card above or below it.
 */

import React from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  getLightBorder,
  getMutedText,
  getPrimaryText,
  getSecondaryText,
} from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface CardHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Heading text. */
  title: React.ReactNode;
  /** h2 by default; h3 for a card nested under a section heading. */
  as?: "h2" | "h3";
  /** Optional icon before the title. */
  icon?: React.ReactNode;
  /** Optional muted line under the title. */
  description?: React.ReactNode;
  /** Right-aligned controls (an Export button, a filter). */
  actions?: React.ReactNode;
  /** Draw the bottom border. Defaults to true. */
  border?: boolean;
  className?: string;
}

const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  as = "h2",
  icon,
  description,
  actions,
  border = true,
  className = "",
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const Tag = as;
  const sizeClass =
    as === "h3" ? TOKENS.heading.subsection : TOKENS.heading.section;

  const classes = [
    TOKENS.cardHeader.base,
    border ? `border-b ${getLightBorder(isDarkMode)}` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} {...rest}>
      <div className="min-w-0">
        <Tag className={`flex items-center gap-2 ${sizeClass} ${getPrimaryText(isDarkMode)}`}>
          {icon && <span className={getMutedText(isDarkMode)}>{icon}</span>}
          <span>{title}</span>
        </Tag>
        {description && (
          <p className={`mt-1 text-sm ${getSecondaryText(isDarkMode)}`}>{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
};

export default CardHeader;
